import {
  useGetIdentity,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";

import type { PipelineCard, PipelineStage, Priority } from "../../types";
import { AssigneeSelect } from "../AssigneeSelect";
import { DEFAULT_PRIORITY } from "../cardModel";
import { PrioritySelect } from "../PrioritySelect";
import { SourcePicker } from "../SourcePicker";
import { TagPicker } from "../TagPicker";
import { useRefreshBoard } from "../useRefreshBoard";
import { DealsSection, type NewDeal } from "./DealsSection";
import { DocumentsSection } from "./DocumentsSection";
import { DraftTasksSection, type DraftTask } from "./DraftTasksSection";
import { FieldRow } from "./FieldRow";
import { NotesSection } from "./NotesSection";
import { useCreateCard } from "./useCreateCard";

export interface CardCreatePanelProps {
  pipelineId: Identifier;
  stageId: Identifier;
  stages: PipelineStage[];
  onCreated: (card: PipelineCard) => void;
  onCancel: () => void;
}

type Keyed<T> = T & { key: number };

const withKey = <T,>(value: T): Keyed<T> => ({
  ...value,
  key: Date.now() + Math.random(),
});

export const CardCreatePanel = ({
  pipelineId,
  stageId: initialStageId,
  stages,
  onCreated,
  onCancel,
}: CardCreatePanelProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const createCard = useCreateCard();
  const refreshBoard = useRefreshBoard();

  const [name, setName] = useState("");
  const [showNameError, setShowNameError] = useState(false);
  const [stageId, setStageId] = useState(initialStageId);
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [salesId, setSalesId] = useState<Identifier | undefined>(identity?.id);
  const [source, setSource] = useState<string | null>(null);
  const [priority, setPriority] = useState<Priority>(DEFAULT_PRIORITY);
  const [tasks, setTasks] = useState<DraftTask[]>([]);
  const [deals, setDeals] = useState<Keyed<NewDeal>[]>([]);
  const [files, setFiles] = useState<Keyed<{ file: File }>[]>([]);
  const [notes, setNotes] = useState<Keyed<{ text: string }>[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      setShowNameError(true);
      return;
    }
    setIsSaving(true);
    try {
      const { card, failedItems } = await createCard({
        pipelineId,
        stageId,
        name: name.trim(),
        tagIds,
        email,
        phone,
        salesId: salesId ?? identity?.id,
        source,
        priority,
        tasks,
        deals,
        files: files.map((item) => item.file),
        notes: notes.map((item) => item.text),
      });
      await refreshBoard();
      if (failedItems > 0) {
        notify("crm.operations.cards.created_partially", {
          type: "warning",
          messageArgs: { smart_count: failedItems },
        });
      } else {
        notify("crm.operations.cards.created");
      }
      onCreated(card);
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "ra.notification.http_error",
        { type: "error" },
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Input
          autoFocus
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setShowNameError(false);
          }}
          placeholder={translate("crm.operations.placeholders.name")}
          aria-label={`${translate("crm.operations.fields.name")} *`}
          aria-invalid={showNameError}
          className="text-base font-semibold"
        />
        {showNameError && (
          <p className="text-xs text-destructive">
            {translate("ra.validation.required")}
          </p>
        )}
      </div>

      <FieldRow label={translate("crm.operations.fields.stage")}>
        <Select
          value={String(stageId)}
          onValueChange={(value) => {
            const stage = stages.find(
              (candidate) => String(candidate.id) === value,
            );
            if (stage) setStageId(stage.id);
          }}
        >
          <SelectTrigger
            className="h-8 w-full"
            aria-label={translate("crm.operations.fields.stage")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stages.map((stage) => (
              <SelectItem key={stage.id} value={String(stage.id)}>
                {stage.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.tags")}>
        <TagPicker value={tagIds} onChange={setTagIds} />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.email")}>
        <Input
          type="email"
          className="h-8"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder={translate("crm.operations.placeholders.email")}
          aria-label={translate("crm.operations.fields.email")}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.phone")}>
        <Input
          type="tel"
          className="h-8"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder={translate("crm.operations.placeholders.phone")}
          aria-label={translate("crm.operations.fields.phone")}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.assignee")}>
        <AssigneeSelect
          className="h-8 w-full"
          value={salesId ?? identity?.id}
          onChange={setSalesId}
        />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.source")}>
        <SourcePicker value={source} onChange={setSource} />
      </FieldRow>
      <FieldRow label={translate("crm.operations.fields.priority")}>
        <PrioritySelect
          className="h-8 w-full"
          value={priority}
          onChange={setPriority}
        />
      </FieldRow>

      <Separator />
      <DraftTasksSection items={tasks} onChange={setTasks} />
      <DealsSection
        items={deals}
        onAdd={(deal) => setDeals((items) => [...items, withKey(deal)])}
        onDelete={(key) =>
          setDeals((items) => items.filter((item) => item.key !== key))
        }
      />
      <DocumentsSection
        items={files.map((item) => ({ key: item.key, title: item.file.name }))}
        onAttach={(newFiles) =>
          setFiles((items) => [
            ...items,
            ...newFiles.map((file) => withKey({ file })),
          ])
        }
        onDelete={(key) =>
          setFiles((items) => items.filter((item) => item.key !== key))
        }
      />
      <NotesSection
        items={notes}
        onAdd={(text) => setNotes((items) => [...items, withKey({ text })])}
        onDelete={(key) =>
          setNotes((items) => items.filter((item) => item.key !== key))
        }
      />

      <div className="sticky bottom-0 flex justify-end gap-2 border-t bg-background py-3">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {translate("ra.action.cancel")}
        </Button>
        <Button type="button" onClick={handleCreate} disabled={isSaving}>
          {translate("crm.operations.cards.create")}
        </Button>
      </div>
    </div>
  );
};
